import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useDebouncedValue } from '@fv/client-core'
import { type Country, states } from '@fv/models'

import {
  useAddressSearch,
  useCitySearch,
} from '@/features/addresses/useAddressSearch'
import { useHasMexico } from '@/features/settings/account-settings/hooks'
import { useAddressBookLocationFilterOptions } from '@/features/shipment-filters/hooks/useLazyFilterOptions'

import { RadioField } from '../inputs/RadioField'
import { ShipperMultiSelect } from '../inputs/ShipperMultiSelect'
import { ShipperTooltip } from '../ShipperTooltip'
import {
  LazyMultiSelectFilter,
  type SubFilterProps,
} from './LazyMultiSelectFilter'
import { MultiSelectFilter } from './MultiSelectFilter'
import type { AnyFilterComponentProps, FilterSelectOption } from './types'

type LocationType = 'Account' | 'City' | 'State' | 'Zip' | 'Country'

function useMultiFilterState({ name, value, setQueryString }: SubFilterProps) {
  const selectedOptions = useMemo(() => {
    const filtered = value.filter(v => v !== '')
    return filtered.map(v => ({ text: v, value: v }))
  }, [value])

  const shouldAutoFocus = useRef(value.filter(v => v !== '').length === 0)

  const onChange = (next: FilterSelectOption[]) => {
    setQueryString(qs => {
      const params = new URLSearchParams(qs)
      params.delete(name)
      next.forEach(opt => params.append(name, opt.value))
      return params.toString()
    })
  }

  return { selectedOptions, shouldAutoFocus: shouldAutoFocus.current, onChange }
}

function AccountLocMultiSelect(props: SubFilterProps) {
  return (
    <LazyMultiSelectFilter
      {...props}
      useOptions={useAddressBookLocationFilterOptions}
    />
  )
}

function StateMultiSelect({ name, value, setQueryString }: SubFilterProps) {
  const hasMexico = useHasMexico()

  const options = useMemo(
    () =>
      states
        .filter(s => hasMexico || s.country !== 'mx')
        .map(s => ({
          text: `${s.name} (${s.abbreviation})`,
          value: s.abbreviation,
        })),
    [hasMexico],
  )

  return (
    <MultiSelectFilter
      name={name}
      options={options}
      value={value}
      setQueryString={setQueryString}
    />
  )
}

const countryOptions: (FilterSelectOption & { country: Country })[] = [
  { country: 'us', text: 'United States (US)', value: 'US' },
  { country: 'ca', text: 'Canada (CA)', value: 'CA' },
  { country: 'mx', text: 'Mexico (MX)', value: 'MX' },
]

function CountryMultiSelect({ name, value, setQueryString }: SubFilterProps) {
  const hasMexico = useHasMexico()

  const options = useMemo(
    () =>
      countryOptions
        .filter(c => hasMexico || c.country !== 'mx')
        .map(({ text, value }) => ({ text, value })),
    [hasMexico],
  )

  return (
    <MultiSelectFilter
      name={name}
      options={options}
      value={value}
      setQueryString={setQueryString}
    />
  )
}

type SearchableMultiSelectOpts<T> = {
  useSearch: (search: string) => { data: T; isFetching: boolean }
  mapOptions: (data: T) => FilterSelectOption[]
}
const createSearchableMultiSelect =
  <T,>({ useSearch, mapOptions }: SearchableMultiSelectOpts<T>) =>
  (props: SubFilterProps) => {
    const [searchText, setSearchText] = useState('')
    const debouncedSearch = useDebouncedValue(searchText, 300)
    const searchQuery = useSearch(debouncedSearch)

    const options = useMemo(() => {
      if (!searchQuery.data) return []
      return mapOptions(searchQuery.data)
    }, [searchQuery])

    const { selectedOptions, shouldAutoFocus, onChange } =
      useMultiFilterState(props)

    return (
      <div className="mt-2">
        <ShipperMultiSelect
          name={props.name}
          options={options}
          value={selectedOptions}
          onChange={onChange}
          onInputValueChange={setSearchText}
          getOptionLabel={o => o.text}
          getOptionValue={o => o.value}
          isLoading={searchQuery.isFetching}
          placeholder="Type to search..."
          autoFocus={shouldAutoFocus}
          emptyMessage="No results found."
        />
      </div>
    )
  }

const useCitySearchString = (search: string) => useCitySearch({ city: search })
const CityMultiSelect = createSearchableMultiSelect({
  useSearch: useCitySearchString,
  mapOptions: data =>
    data.map(a => {
      const text = `${a.city}, ${a.state}`
      return { text, value: text }
    }),
})

const usePostalCodeSearch = (search: string) =>
  useAddressSearch({
    search: search,
    includeAddressBook: false,
    includeAtlas: true,
  })

const PostalCodeMultiSelect = createSearchableMultiSelect({
  useSearch: usePostalCodeSearch,
  mapOptions: data => {
    const zips = [...new Set(data.map(r => r.postalCode))]
    return zips.sort().map(zip => ({ text: zip, value: zip }))
  },
})

type LocationFilterProps = {
  locationType: 'origin' | 'dest'
  setQueryString: Dispatch<SetStateAction<string>>
  queryString?: string
}

const LocationFilter = ({
  locationType,
  setQueryString,
  queryString = '',
}: LocationFilterProps) => {
  const [selectedType, setSelectedType] = useState<LocationType>('Account')

  const getName = (type: LocationType) => `${locationType}${type}[]`

  useEffect(() => {
    const params = new URLSearchParams(queryString)
    if (params.has(`${locationType}Account[]`)) {
      setSelectedType('Account')
    } else if (params.has(`${locationType}City[]`)) {
      setSelectedType('City')
    } else if (params.has(`${locationType}Zip[]`)) {
      setSelectedType('Zip')
    } else if (params.has(`${locationType}State[]`)) {
      setSelectedType('State')
    } else if (params.has(`${locationType}Country[]`)) {
      setSelectedType('Country')
    }
  }, [locationType, queryString])

  const currentName = getName(selectedType)

  const currentValues = useMemo(() => {
    const params = new URLSearchParams(queryString)
    return params.getAll(currentName)
  }, [queryString, currentName])

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as LocationType
    setSelectedType(newType)

    setQueryString(qs => {
      const params = new URLSearchParams(qs)
      params.delete(getName('Account'))
      params.delete(getName('City'))
      params.delete(getName('State'))
      params.delete(getName('Zip'))
      params.delete(getName('Country'))
      return params.toString()
    })
  }

  const subFilterProps: SubFilterProps = {
    name: currentName,
    value: currentValues,
    setQueryString,
  }

  return (
    <>
      <div
        className="my-2"
        onMouseDownCapture={event => {
          event.preventDefault()
        }}
      >
        <RadioField
          className="flex-wrap gap-y-2"
          name={`${locationType}Type`}
          value={selectedType}
          onChange={handleTypeChange}
          options={[
            {
              label: (
                <ShipperTooltip label="Account location">
                  <span>Account</span>
                </ShipperTooltip>
              ),
              value: 'Account',
            },
            'City',
            'State',
            'Zip',
            'Country',
          ]}
          alternate
        />
      </div>
      {selectedType === 'Account' && (
        <AccountLocMultiSelect key={currentName} {...subFilterProps} />
      )}
      {selectedType === 'City' && (
        <CityMultiSelect key={currentName} {...subFilterProps} />
      )}
      {selectedType === 'State' && (
        <StateMultiSelect key={currentName} {...subFilterProps} />
      )}
      {selectedType === 'Zip' && (
        <PostalCodeMultiSelect key={currentName} {...subFilterProps} />
      )}
      {selectedType === 'Country' && (
        <CountryMultiSelect key={currentName} {...subFilterProps} />
      )}
    </>
  )
}

const createLocFilterComponent =
  (locationType: 'origin' | 'dest') =>
  ({ setQueryString, queryString }: AnyFilterComponentProps) => (
    <LocationFilter
      locationType={locationType}
      setQueryString={setQueryString}
      queryString={queryString}
    />
  )

export const OriginFilterComponent = createLocFilterComponent('origin')
export const DestFilterComponent = createLocFilterComponent('dest')
